import { useId, useState } from 'react'
import { cn } from '~/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'

import MarkDown from '~/components/Helpers/MarkDown'
import ControlButton from '~/components/UI/Buttons/ControlButton/ControlButton'

type OverlayProps = {
  overlayText: string
  colour: VariantProps<typeof overlayVariants>['colour']
  onClick?: () => void
}

const overlayVariants = cva('', {
  variants: {
    colour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-blue': 'bg-blue-l text-blue-d',
      'light-pink': 'bg-pink text-blue-d',
      'light-aqua': 'bg-green text-blue-d',
      'light-purple': 'bg-purple text-blue-d',
      'light-orange': 'bg-orange text-blue-d',
      white: 'bg-white text-blue-d'
    }
  },
  defaultVariants: {
    colour: 'white'
  }
})

const CardOverlay = ({ overlayText, colour, onClick }: OverlayProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const maskId = useId()

  function toggleOverlay() {
    setIsVisible(!isVisible)
  }

  return (
    <>
      <ControlButton
        onClick={() => {
          toggleOverlay(), onClick && onClick()
        }}
        data={{
          ariaLabel: 'Open overlay',
          icon: {
            asset: {
              sys: {
                uri: ['ink-blue', 'mid-blue'].includes(colour ?? '')
                  ? '/site-elements/icons/single-colour/white/plus.svg'
                  : '/site-elements/icons/single-colour/ink-blue/plus.svg'
              }
            },
            altText: ''
          }
        }}
        colour={colour}
        size='large'
        className={cn(
          'z-1 size-fit shrink-0 self-end [&_img]:transition-transform [&_img]:duration-500',
          isVisible ? '[&_img]:rotate-45' : ''
        )}
      />

      <div
        className={cn(
          overlayVariants({ colour }),
          'sm:p-s p-xs-plus lg:p-m absolute bottom-0 left-0 h-full w-full transition-all',
          isVisible ? 'z-0' : '-z-1 delay-500'
        )}
        style={{
          mask: `url(#${maskId})`,
          WebkitMask: `url(#${maskId})`
        }}
      >
        <MarkDown className='space-y-xs' content={overlayText} />
        <svg
          width='100%'
          height='100%'
          className='absolute top-0 left-0 z-[-1]'
        >
          <mask id={maskId}>
            <circle
              r='140%'
              cx='50%'
              cy='50%'
              fill='white'
              className={cn(
                'origin-center translate-x-[calc(50%-34px)] translate-y-[calc(50%-40px)] transition-transform duration-500 sm:translate-x-[calc(50%-44px)] sm:translate-y-[calc(50%-50px)] lg:translate-x-[calc(50%-54px)] lg:translate-y-[calc(50%-60px)]',
                isVisible ? 'scale-100' : 'scale-0'
              )}
            />
          </mask>
        </svg>
      </div>
    </>
  )
}

export default CardOverlay
